import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import {
  ArrowUp, BarChart3, ChevronDown, Copy, FilePlus2, FileText, LayoutTemplate, ListChecks, Loader2, MoreHorizontal,
  PenLine, Presentation, RotateCcw, Sigma, StickyNote, Table2, Wand2, X,
} from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../ui/dropdown-menu';
import { AuroraLogo } from './icons';
import {
  askAuroraAI, errorMessage, insertAIResult, markdownTableRows, markdownToHtml, readEditorContext,
} from './officeAI';

const DOC_TEMPLATES = ['Acta de reunión', 'Propuesta comercial', 'Carta formal', 'Informe ejecutivo', 'Plan de proyecto', 'Minuta de acuerdos'];

const COMPOSER = {
  doc: {
    collapsed: 'Escribir con Aurora IA',
    corto: 'Escribir con Aurora',
    placeholder: 'Escribe con Aurora IA… (si seleccionas texto, lo reescribe)',
    defaultAction: 'write',
    chips: [
      { label: 'Mejorar redacción', icon: Wand2, action: 'improve', selection: true },
      { label: 'Igualar estilo', icon: PenLine, action: 'match_style', selection: true },
      { label: 'Plantillas', icon: LayoutTemplate, menu: DOC_TEMPLATES.map((t) => ({ label: t, action: 'template', prompt: t })) },
      {
        label: 'Más',
        icon: MoreHorizontal,
        menu: [
          { label: 'Continuar escribiendo', action: 'continue' },
          { label: 'Resumir documento', action: 'summarize' },
          { label: 'Corregir ortografía', action: 'fix', selection: true },
          { label: 'Hacer más breve', action: 'shorter', selection: true },
          { label: 'Ampliar', action: 'longer', selection: true },
          { label: 'Traducir', action: 'translate', selection: true },
        ],
      },
    ],
  },
  sheet: {
    collapsed: 'Pregunta a Aurora IA',
    corto: 'Pregunta a Aurora',
    placeholder: 'Pide una tabla, una fórmula o un análisis…',
    defaultAction: 'auto',
    chips: [
      { label: 'Crear tabla', icon: Table2, action: 'table', mode: '¿Qué tabla necesitas? Ej. inventario con producto, precio y stock' },
      { label: 'Fórmula', icon: Sigma, action: 'formula', mode: '¿Qué debe calcular la celda activa?' },
      { label: 'Analizar datos', icon: BarChart3, action: 'analyze' },
    ],
  },
  pdf: {
    collapsed: 'Pregunta a Aurora IA sobre el PDF',
    corto: 'Pregunta a Aurora',
    placeholder: 'Pregunta lo que quieras sobre este PDF…',
    defaultAction: 'ask',
    chips: [
      { label: 'Resumir PDF', icon: FileText, action: 'summarize' },
      { label: 'Puntos clave', icon: ListChecks, action: 'key_points' },
      { label: 'Extraer datos', icon: Table2, action: 'extract' },
    ],
  },
  slides: {
    collapsed: 'Crear una diapositiva',
    corto: 'Crear con Aurora',
    placeholder: 'Describe la diapositiva que quieres crear…',
    defaultAction: 'slide',
    chips: [
      { label: 'Crear diapositiva', icon: FilePlus2, action: 'slide', mode: 'Describe la diapositiva…' },
      { label: 'Presentación completa', icon: Presentation, action: 'deck', mode: 'Tema de la presentación…' },
      { label: 'Mejorar diapositiva', icon: Wand2, action: 'improve' },
      { label: 'Notas del orador', icon: StickyNote, action: 'notes' },
    ],
  },
};

/** Textos del botón que abre la barra desde la cabecera (O10): `corto` a la vista, `completo` para el lector de pantalla. */
export function textosAurora(kind) {
  const cfg = COMPOSER[kind] || COMPOSER.doc;
  return { corto: cfg.corto, completo: cfg.collapsed };
}

function allChips(kind) {
  return COMPOSER[kind].chips.flatMap((c) => (c.menu ? c.menu : [c]));
}

function rowsToTsv(rows) {
  return rows.map((r) => r.join('\t')).join('\n');
}

function ResultCard({ kind, result, inserting, canEdit, onInsert, onRetry, onDiscard }) {
  const copy = async () => {
    const text = result.type === 'table' ? rowsToTsv(result.rows) : result.type === 'formula' ? result.formula
      : result.type === 'slides' ? result.slides.map((s) => `${s.title}\n${s.bullets.map((b) => `• ${b}`).join('\n')}`).join('\n\n')
        : result.text;
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Copiado');
    } catch {
      toast.error('No se pudo copiar');
    }
  };

  const tableRows = result.type === 'markdown' && kind === 'sheet' ? markdownTableRows(result.text) : [];
  let primary = null;
  if (canEdit) {
    if (result.type === 'markdown' && kind === 'doc') {
      primary = [
        { label: result.hadSelection ? 'Reemplazar selección' : 'Insertar', mode: 'cursor' },
        { label: 'Insertar al final', mode: 'append', secondary: true },
      ];
    } else if (result.type === 'formula') {
      primary = [{ label: `Insertar en ${result.target || 'la celda activa'}` }];
    } else if (result.type === 'table') {
      primary = [{ label: 'Insertar tabla' }];
    } else if (tableRows.length > 1) {
      primary = [{ label: 'Insertar tabla', asTable: true }];
    } else if (result.type === 'slides') {
      primary = [{ label: result.slides.length === 1 ? 'Agregar diapositiva' : `Agregar ${result.slides.length} diapositivas` }];
    }
  }

  return (
    <div className="zo-ai-card" data-testid="zo-ai-result">
      <div className="zo-ai-card-body">
        {result.type === 'markdown' && (
          // markdownToHtml escapa todo el texto antes de agregar etiquetas
          <div className="zo-md" dangerouslySetInnerHTML={{ __html: markdownToHtml(result.text) }} />
        )}
        {result.type === 'formula' && (
          <div>
            <code className="zo-formula">{result.formula}</code>
            {result.text && <p className="zo-muted">{result.text}</p>}
          </div>
        )}
        {result.type === 'table' && (
          <div>
            {result.text && <p className="zo-muted">{result.text}</p>}
            <div className="zo-table-wrap">
              <table className="zo-table">
                <tbody>
                  {result.rows.slice(0, 8).map((row, i) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <tr key={i}>{row.map((c, j) => (i === 0 ? <th key={j}>{String(c)}</th> : <td key={j}>{String(c)}</td>))}</tr>
                  ))}
                </tbody>
              </table>
            </div>
            {result.rows.length > 8 && <p className="zo-muted">+{result.rows.length - 8} filas más</p>}
          </div>
        )}
        {result.type === 'slides' && (
          <div className="zo-slide-previews">
            {result.slides.map((s, i) => (
              // eslint-disable-next-line react/no-array-index-key
              <div key={i} className="zo-slide-preview">
                <div className="zo-slide-preview-title">{s.title}</div>
                <ul>{s.bullets.slice(0, 4).map((b, j) => <li key={j}>{b}</li>)}</ul>
              </div>
            ))}
          </div>
        )}
      </div>
      <div className="zo-ai-card-actions">
        {primary?.map((p) => (
          <button
            key={p.label}
            type="button"
            className={p.secondary ? 'zo-btn-tonal' : 'zo-btn-primary'}
            disabled={inserting}
            onClick={() => onInsert(p)}
          >
            {inserting && !p.secondary ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            {p.label}
          </button>
        ))}
        <span className="zo-spacer" />
        <button type="button" className="zo-icon-btn" title="Copiar" onClick={copy}><Copy className="h-4 w-4" /></button>
        <button type="button" className="zo-icon-btn" title="Volver a generar" onClick={onRetry}><RotateCcw className="h-4 w-4" /></button>
        <button type="button" className="zo-icon-btn" title="Descartar" onClick={onDiscard}><X className="h-4 w-4" /></button>
      </div>
    </div>
  );
}

/** Barra flotante "Escribir con Aurora IA" (abajo al centro, como en Docs/Slides). */
/**
 * `soloAbierto`: sin píldora flotante (tapaba el documento). Se abre con `request = { action: 'open' }` desde
 * el botón Aurora IA de la barra inferior (teléfono) o de la cabecera (O10), `{ action: 'cerrar' }` la pliega
 * y al plegarse desaparece. `onAbierto(bool)` le dice a la cabecera si está abierta.
 */
export default function AIComposer({ kind, fileId, bridge, canEdit, request, soloAbierto = false, onPlegar, onAbierto }) {
  const cfg = COMPOSER[kind] || COMPOSER.doc;
  const [expanded, setExpanded] = useState(false);
  // O10 (auditoría 2026-10-09): al plegarse, el foco vuelve al documento (Ctrl+S y la escritura se quedaban fuera)
  const onPlegarRef = useRef(onPlegar);
  onPlegarRef.current = onPlegar;
  const plegar = () => {
    setExpanded(false);
    onPlegarRef.current?.();
  };
  const onAbiertoRef = useRef(onAbierto);
  onAbiertoRef.current = onAbierto;
  useEffect(() => {
    onAbiertoRef.current?.(expanded);
  }, [expanded]);
  useEffect(() => () => onAbiertoRef.current?.(false), []);
  const [mode, setMode] = useState(null);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(null);
  const [result, setResult] = useState(null);
  const [inserting, setInserting] = useState(false);
  const inputRef = useRef(null);
  const lastRun = useRef(null);

  const run = async (chip, prompt = '') => {
    lastRun.current = { chip, prompt };
    setExpanded(true);
    setBusy(chip.label || 'Aurora IA');
    setResult(null);
    try {
      const ctx = await readEditorContext(bridge, kind);
      if (chip.selection && !ctx.selection) {
        toast.message('Primero selecciona el texto en el documento');
        return;
      }
      let action = chip.action;
      if (kind === 'doc' && action === 'write' && ctx.selection) action = 'rewrite';
      const res = await askAuroraAI({
        file_id: fileId,
        kind,
        action,
        prompt: prompt || chip.prompt || '',
        selection: ctx.selection || '',
        context: ctx.context || '',
        current: ctx.current || '',
      });
      setResult({ ...res, deck: action === 'deck', hadSelection: !!ctx.selection, target: ctx.target });
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusy(null);
    }
  };

  // Acciones pedidas desde fuera (panel lateral de Slides)
  useEffect(() => {
    if (!request) return;
    if (request.action === 'cerrar') {
      plegar();
      return;
    }
    if (request.action === 'open') {
      setExpanded(true);
      setTimeout(() => inputRef.current?.focus(), 50);
      return;
    }
    const chip = allChips(kind).find((c) => c.action === request.action);
    if (!chip) return;
    setExpanded(true);
    if (chip.mode) {
      setMode(chip);
      setResult(null);
      setTimeout(() => inputRef.current?.focus(), 50);
    } else {
      run(chip);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [request]);

  const submit = (e) => {
    e.preventDefault();
    const prompt = text.trim();
    if (!prompt || busy) return;
    const chip = mode || { label: 'Aurora IA', action: cfg.defaultAction };
    setText('');
    run(chip, prompt);
  };

  const onChip = (chip) => {
    if (chip.mode) {
      setMode(chip);
      setTimeout(() => inputRef.current?.focus(), 50);
    } else {
      run(chip);
    }
  };

  const insert = async (option) => {
    setInserting(true);
    try {
      const where = await insertAIResult(bridge, { ...result, asTable: option.asTable }, option.mode || 'cursor');
      if (kind === 'slides') toast.success('Diapositivas agregadas');
      else if (kind === 'sheet' && typeof where === 'string') toast.success(`Insertado en ${where}`);
      else toast.success('Insertado');
      setResult(null);
      setMode(null);
    } catch (err) {
      toast.error(errorMessage(err, 'No se pudo insertar'));
    } finally {
      setInserting(false);
    }
  };

  useEffect(() => {
    if (!expanded) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        setExpanded(false);
        onPlegarRef.current?.();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [expanded]);

  if (!expanded) {
    if (soloAbierto) return null;
    return (
      <div className="zo-composer">
        <button type="button" className="zo-composer-pill" onClick={() => setExpanded(true)} data-testid="zo-composer-open">
          <AuroraLogo size={16} /> {cfg.collapsed}
        </button>
      </div>
    );
  }

  return (
    <div className="zo-composer is-open" data-testid="zo-composer">
      {busy && (
        <div className="zo-ai-card zo-ai-thinking">
          <AuroraLogo size={18} />
          <span>{busy === 'Aurora IA' ? 'Aurora IA está escribiendo' : `${busy}`}…</span>
          <div className="zo-shimmer" />
        </div>
      )}
      {result && !busy && (
        <ResultCard
          kind={kind}
          result={result}
          inserting={inserting}
          canEdit={canEdit}
          onInsert={insert}
          onRetry={() => lastRun.current && run(lastRun.current.chip, lastRun.current.prompt)}
          onDiscard={() => setResult(null)}
        />
      )}
      {!result && !busy && (
        <div className="zo-chips">
          {cfg.chips.map((chip) => {
            const Icon = chip.icon;
            if (chip.menu) {
              return (
                <DropdownMenu key={chip.label}>
                  <DropdownMenuTrigger asChild>
                    <button type="button" className="zo-chip"><Icon className="h-4 w-4" /> {chip.label}</button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent side="top" align="center" className="zo-menu">
                    {chip.menu.map((item) => (
                      <DropdownMenuItem key={item.label} onSelect={() => run(item)}>{item.label}</DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              );
            }
            return (
              <button key={chip.label} type="button" className={`zo-chip ${mode === chip ? 'is-active' : ''}`} onClick={() => onChip(chip)}>
                <Icon className="h-4 w-4" /> {chip.label}
              </button>
            );
          })}
        </div>
      )}
      <form className="zo-composer-bar" onSubmit={submit}>
        <AuroraLogo size={18} />
        {mode && (
          <span className="zo-mode-tag">
            {mode.label}
            <button type="button" aria-label="Quitar" onClick={() => setMode(null)}><X className="h-3 w-3" /></button>
          </span>
        )}
        <input
          ref={inputRef}
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={mode?.mode || cfg.placeholder}
          aria-label="Instrucción para Aurora IA"
        />
        <button type="button" className="zo-icon-btn" title="Ocultar" onClick={plegar}>
          <ChevronDown className="h-4 w-4" />
        </button>
        <button type="submit" className="zo-send" disabled={!text.trim() || !!busy} aria-label="Enviar">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowUp className="h-4 w-4" />}
        </button>
      </form>
    </div>
  );
}
